import { useMemo, useState } from 'react';
import {
  Menu,
  TextInput,
  ScrollArea,
  Checkbox,
  Divider,
  Box,
  Text,
  SegmentedControl,
  Radio,
  Stack,
} from '@mantine/core';
import {
  IconFilter,
  IconSearch,
  IconChevronDown,
  IconChevronUp,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  EMPTY_FILTER_ITEMS,
  useFilterDropdownState,
} from '../hooks/useFilterDropdownState';
import { FilterDropdownActions } from './FilterDropdownActions';
import { MarketplaceToolbarButton } from './MarketplaceToolbarButton';

export type VisibilityFilterValue = 'all' | 'public' | 'private';

type FilterSection = 'visibility' | 'company' | 'category' | 'tags';

const noopSelectionChange = (_items: string[]) => undefined;

export interface MarketplaceFiltersDropdownProps {
  /** Omit or leave empty to hide the company section (e.g. dashboard). */
  companies?: string[];
  selectedCompanies?: string[];
  onCompaniesChange?: (companies: string[]) => void;
  tags: string[];
  tagCounts?: Record<string, number>;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  categories?: string[];
  categoryCounts?: Record<string, number>;
  selectedCategories?: string[];
  onCategoriesChange?: (categories: string[]) => void;
  /** Dashboard visibility filter (all / public / private). */
  visibilityFilter?: VisibilityFilterValue;
  onVisibilityChange?: (value: VisibilityFilterValue) => void;
  accentColor?: string;
  isMobile?: boolean;
}

/**
 * Shared "Filters" control with tabbed sections (visibility / company / category / tags).
 * Only one list is shown at a time; checkbox/radio clicks apply immediately.
 */
export function MarketplaceFiltersDropdown({
  companies = EMPTY_FILTER_ITEMS,
  selectedCompanies = EMPTY_FILTER_ITEMS,
  onCompaniesChange,
  tags,
  tagCounts,
  selectedTags,
  onTagsChange,
  categories = EMPTY_FILTER_ITEMS,
  categoryCounts,
  selectedCategories = EMPTY_FILTER_ITEMS,
  onCategoriesChange,
  visibilityFilter = 'all',
  onVisibilityChange,
  accentColor = 'blue',
  isMobile = false,
}: MarketplaceFiltersDropdownProps) {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);

  const showVisibility = Boolean(onVisibilityChange);
  const showCompany = Boolean(onCompaniesChange) && companies.length > 0;
  // Temporarily hidden — categories UI disabled
  // const showCategories = Boolean(onCategoriesChange) && categories.length > 0;

  const defaultSection: FilterSection = showVisibility
    ? 'visibility'
    : showCompany
      ? 'company'
      : // showCategories ? 'category' :
        'tags';

  const [section, setSection] = useState<FilterSection>(defaultSection);

  const companyFilter = useFilterDropdownState({
    items: companies,
    selectedItems: selectedCompanies,
    onSelectionChange: onCompaniesChange ?? noopSelectionChange,
    opened,
    setOpened,
  });

  const tagFilter = useFilterDropdownState({
    items: tags,
    selectedItems: selectedTags,
    onSelectionChange: onTagsChange,
    opened,
    setOpened,
  });

  const categoryFilter = useFilterDropdownState({
    items: categories,
    selectedItems: selectedCategories,
    onSelectionChange: onCategoriesChange ?? noopSelectionChange,
    opened,
    setOpened,
  });

  const sortedFilteredTags = useMemo(
    () =>
      [...tagFilter.filteredItems].sort(
        (a, b) => (tagCounts?.[b] ?? 0) - (tagCounts?.[a] ?? 0)
      ),
    [tagFilter.filteredItems, tagCounts]
  );

  /* Temporarily hidden — categories UI disabled
  const filteredCategoriesByLabel = useMemo(() => {
    const q = categoryFilter.searchQuery.trim().toLowerCase();
    const list = !q
      ? categories
      : categories.filter((slug) => {
          const label = t(`productCategories.${slug}`, { defaultValue: slug }).toLowerCase();
          return label.includes(q) || slug.toLowerCase().includes(q);
        });
    return [...list].sort(
      (a, b) => (categoryCounts?.[b] ?? 0) - (categoryCounts?.[a] ?? 0)
    );
  }, [categories, categoryFilter.searchQuery, categoryCounts, t]);
  */

  const activeCount =
    selectedCompanies.length +
    selectedTags.length +
    // Temporarily hidden — categories UI disabled
    // selectedCategories.length +
    (showVisibility && visibilityFilter !== 'all' ? 1 : 0);
  const hasActiveFilter = activeCount > 0;

  const handleOpenChange = (open: boolean) => {
    companyFilter.handleOpenChange(open);
    tagFilter.handleOpenChange(open);
    categoryFilter.handleOpenChange(open);
    if (open) {
      setSection(defaultSection);
    }
  };

  const handleReset = () => {
    companyFilter.handleReset();
    tagFilter.handleReset();
    categoryFilter.handleReset();
    onVisibilityChange?.('all');
  };

  const sectionData = [
    ...(showVisibility
      ? [{ label: t('dashboard.visibility.filter'), value: 'visibility' }]
      : []),
    ...(showCompany ? [{ label: t('marketplace.search.company'), value: 'company' }] : []),
    // Temporarily hidden — categories UI disabled
    // ...(showCategories
    //   ? [{ label: t('marketplace.search.category'), value: 'category' }]
    //   : []),
    { label: t('marketplace.search.tags'), value: 'tags' },
  ];

  const showSearch = section !== 'visibility';
  const activeSearch =
    section === 'company'
      ? companyFilter
      : // section === 'category' ? categoryFilter :
        tagFilter;

  const searchPlaceholder =
    section === 'company'
      ? t('marketplace.search.companySearchPlaceholder')
      : // section === 'category'
        //   ? t('marketplace.search.categorySearchPlaceholder')
        //   :
        t('marketplace.search.tagSearchPlaceholder');

  return (
    <Menu
      opened={opened}
      onChange={handleOpenChange}
      width={320}
      position="bottom-end"
      shadow="md"
      floatingStrategy="fixed"
      closeOnClickOutside
      closeOnEscape
    >
      <Menu.Target>
        <MarketplaceToolbarButton
          accentColor={accentColor}
          active={hasActiveFilter}
          hideLabel={isMobile}
          leftIcon={<IconFilter size={16} stroke={1.75} />}
          rightIcon={
            opened ? (
              <IconChevronUp size={15} stroke={1.75} />
            ) : (
              <IconChevronDown size={15} stroke={1.75} />
            )
          }
          badge={hasActiveFilter ? activeCount : undefined}
        >
          {t('marketplace.search.filters')}
        </MarketplaceToolbarButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Box p="xs" pb="sm">
          <SegmentedControl
            fullWidth
            size="xs"
            value={section}
            onChange={(value) => setSection(value as FilterSection)}
            data={sectionData}
            styles={{
              root: { flexWrap: 'wrap' },
              label: { whiteSpace: 'nowrap', paddingInline: 6 },
            }}
          />
        </Box>

        {showSearch ? (
          <Box px="xs" pb={0}>
            <TextInput
              placeholder={searchPlaceholder}
              value={activeSearch.searchQuery}
              onChange={(e) => activeSearch.setSearchQuery(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              size="sm"
              autoFocus
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
        ) : null}

        <ScrollArea.Autosize mah={280} type="scroll" viewportProps={{ tabIndex: -1 }}>
          <Box py="xs" px="sm">
            {section === 'visibility' && showVisibility ? (
              <Radio.Group
                value={visibilityFilter}
                onChange={(value) => onVisibilityChange?.(value as VisibilityFilterValue)}
              >
                <Stack gap="xs">
                  <Radio value="all" label={t('dashboard.visibility.all')} size="sm" />
                  <Radio value="public" label={t('dashboard.visibility.public')} size="sm" />
                  <Radio value="private" label={t('dashboard.visibility.private')} size="sm" />
                </Stack>
              </Radio.Group>
            ) : null}

            {section === 'company' && showCompany ? (
              companyFilter.filteredItems.length === 0 ? (
                <Text size="sm" c="dimmed" py="md">
                  {t('marketplace.search.noCompaniesMatch')}
                </Text>
              ) : (
                companyFilter.filteredItems.map((company) => (
                  <Checkbox
                    key={company}
                    label={company}
                    checked={companyFilter.selection.has(company)}
                    onChange={() => companyFilter.handleToggle(company)}
                    size="sm"
                    mb="xs"
                    styles={{ label: { cursor: 'pointer', width: '100%' } }}
                  />
                ))
              )
            ) : null}

            {/* Temporarily hidden — categories UI disabled
            {section === 'category' && showCategories ? (
              filteredCategoriesByLabel.length === 0 ? (
                <Text size="sm" c="dimmed" py="md">
                  {t('marketplace.search.noCategoriesMatch')}
                </Text>
              ) : (
                filteredCategoriesByLabel.map((slug) => {
                  const label = t(`productCategories.${slug}`, { defaultValue: slug });
                  return (
                    <Checkbox
                      key={slug}
                      label={
                        categoryCounts && categoryCounts[slug] != null
                          ? `${label} (${categoryCounts[slug]})`
                          : label
                      }
                      checked={categoryFilter.selection.has(slug)}
                      onChange={() => categoryFilter.handleToggle(slug)}
                      size="sm"
                      mb="xs"
                      styles={{ label: { cursor: 'pointer', width: '100%' } }}
                    />
                  );
                })
              )
            ) : null}
            */}

            {section === 'tags' ? (
              sortedFilteredTags.length === 0 ? (
                <Text size="sm" c="dimmed" py="md">
                  {t('marketplace.search.noTagsMatch')}
                </Text>
              ) : (
                sortedFilteredTags.map((tag) => (
                  <Checkbox
                    key={tag}
                    label={
                      tagCounts && tagCounts[tag] != null ? `${tag} (${tagCounts[tag]})` : tag
                    }
                    checked={tagFilter.selection.has(tag)}
                    onChange={() => tagFilter.handleToggle(tag)}
                    size="sm"
                    mb="xs"
                    styles={{ label: { cursor: 'pointer', width: '100%' } }}
                  />
                ))
              )
            ) : null}
          </Box>
        </ScrollArea.Autosize>

        <Divider />

        <FilterDropdownActions accentColor={accentColor} onReset={handleReset} />
      </Menu.Dropdown>
    </Menu>
  );
}
